import { z } from 'zod';
import { IsoDatetime } from './primitives.js';
import { TrustLevel } from './trust.js';

const Id = z.string().max(40);

// The actuation ladder (decision 5, 01 §8): 1 Meta API · 2 Baileys ·
// 3 voice · 4 browser · 5 human.
export const ActuationLevel = z.number().int().min(1).max(5);
export type ActuationLevel = z.infer<typeof ActuationLevel>;

export const ActuationChannel = z.enum(['meta_whatsapp', 'baileys', 'voice', 'browser', 'human']);
export type ActuationChannel = z.infer<typeof ActuationChannel>;

export const ActuationActionKind = z.enum([
  'notify',
  'inquire',
  'book',
  'order',
  'pay',
  'navigate',
]);
export type ActuationActionKind = z.infer<typeof ActuationActionKind>;

export const ActuationResult = z.enum(['success', 'failed', 'timeout', 'escalated_down']);
export type ActuationResult = z.infer<typeof ActuationResult>;

// A typed action: what was attempted and what was actually said/sent.
// Never credentials or tokens (03 §7.1).
export const ActuationAction = z.object({
  kind: ActuationActionKind,
  intent: z.record(z.any()),
  rendered_content: z.string().optional(),
});
export type ActuationAction = z.infer<typeof ActuationAction>;

// Every action in the world on the household's behalf: channel, content,
// ladder level, result, approval (01 §8). Append-only — a product acting in
// the owners' name lives on "what was said in my name?".
export const ActuationJournalEntry = z
  .object({
    id: Id,
    household_id: Id,
    situation_id: Id.nullable(),
    // Who acted: an AI agent or field staff (agent-staff is journaled too).
    actor_agent_key: z.string().max(80).nullable(),
    actor_staff_id: Id.nullable(),
    // To whom/what: vendor, staff, institution — an entity in the graph.
    target_entity_id: Id.nullable(),
    // 1,2,3... within the situation (the ladder descends 1→2→3...).
    attempt_no: z.number().int().min(1).default(1),
    // 'engine' | 'owner' | 'protocol:<name>'.
    initiated_by: z.string().min(1).default('engine'),
    // situationId + actionKind + attempt — duplicate prevention.
    idempotency_key: z.string().min(8).max(120),
    channel_level: ActuationLevel,
    channel: ActuationChannel,
    action: ActuationAction,
    // The trust level that authorized this action (hard bound, decision 12).
    trust_level_used: TrustLevel,
    approved_by_user_id: Id.nullable(),
    result: ActuationResult,
    // Provider terminal status, outbound ref, next retry — the detail.
    result_detail: z.record(z.any()).nullable(),
    // Marginal attempt cost (Twilio minutes, Meta messages) in minor units.
    cost_minor: z.number().int().nullable(),
    // Evidence: transcript/recording/screenshot R2 keys.
    evidence_r2_keys: z.array(z.string()).default([]),
    // The previous attempt in the same situation — the attempt chain.
    previous_attempt_id: Id.nullable(),
    // S61: did the action disclose household identity to a third party?
    disclosed_identity: z.boolean().default(false),
    created_at: IsoDatetime,
  })
  .refine(
    (entry) => entry.channel_level < 4 || entry.approved_by_user_id !== null,
    'browser-level (4) actuation requires an owner approval before committing',
  )
  .refine(
    (entry) => entry.channel_level < 3 || entry.evidence_r2_keys.length > 0,
    'ladder levels 3-4 require evidence keys (transcript/recording/screenshot)',
  );
export type ActuationJournalEntry = z.infer<typeof ActuationJournalEntry>;
